import { useState, useEffect, useCallback } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react'
import { getAuth, signOut } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'

import AuthHeader from '../../components/AuthHeader'
import useStore from '../../store/useStore'
import OTPInput from './components/OTPInput'
import ResendTimer from './components/ResendTimer'
import { useVerifyOTP, useResendOTP } from './hooks'
import { OTP_LENGTH, MAX_ATTEMPTS } from '../../constants/emailVerification'

export default function VerifyEmailPage() {
  // 1. State
  const [otp, setOtp] = useState('')

  // 2. Context and navigation
  const user = useStore((state) => state.user)
  const firestoreUser = useStore((state) => state.firestoreUser)
  const navigate = useNavigate()

  // 3. Custom hooks
  const {
    verifyOTP,
    loading: verifyLoading,
    error,
    attemptsRemaining,
    resetOTP,
  } = useVerifyOTP()

  const handleResendSuccess = useCallback(() => {
    setOtp('')
    resetOTP()
  }, [resetOTP])

  const {
    resendOTP,
    loading: resendLoading,
    cooldownSeconds,
  } = useResendOTP(handleResendSuccess)

  // 4. Effects
  useEffect(() => {
    if (firestoreUser?.emailVerified === true) {
      navigate('/profile', { replace: true })
    }
  }, [firestoreUser?.emailVerified, navigate])

  useEffect(() => {
    if (otp.length === OTP_LENGTH) {
      verifyOTP(otp).then(() => setOtp(''))
    }
  }, [otp, verifyOTP])

  // 5. Event handlers
  function handleVerify() {
    verifyOTP(otp).then(() => setOtp(''))
  }

  async function handleSignOut() {
    try {
      await signOut(getAuth())
      navigate('/sign-up', { replace: true })
    } catch (err) {
      console.error('Sign out error:', err)
    }
  }

  // 6. Render
  return (
    <Container maxW='md'>
      <Stack spacing={6} py={8}>
        <AuthHeader />

        <Box textAlign='center'>
          <Text fontSize='2xl' fontWeight='bold' color='gray.800'>
            Verify Your Email
          </Text>
          <Text fontSize='md' color='gray.600' mt={2}>
            We've sent a {OTP_LENGTH}-digit verification code to
          </Text>
          <Text fontSize='md' fontWeight='semibold' color='purple.600'>
            {user?.email}
          </Text>
        </Box>

        <Divider />

        <Stack spacing={4}>
          <Text textAlign='center' fontSize='sm' color='gray.600'>
            Enter the verification code:
          </Text>

          <OTPInput
            value={otp}
            onChange={setOtp}
            isDisabled={verifyLoading || attemptsRemaining === 0}
          />

          {error && (
            <Alert status='error' borderRadius='md'>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {attemptsRemaining > 0 && attemptsRemaining < MAX_ATTEMPTS && (
            <Text textAlign='center' fontSize='sm' color='orange.600'>
              {attemptsRemaining} attempts remaining
            </Text>
          )}

          <Button
            colorScheme='purple'
            size='lg'
            onClick={handleVerify}
            isLoading={verifyLoading}
            isDisabled={otp.length !== OTP_LENGTH || attemptsRemaining === 0}
          >
            Verify Email
          </Button>

          <ResendTimer
            cooldownSeconds={cooldownSeconds}
            onResend={resendOTP}
            isLoading={resendLoading}
          />
        </Stack>

        <Divider />

        <Box textAlign='center'>
          <Text fontSize='sm' color='gray.500'>
            Wrong email?{' '}
            <Link color='purple.600' onClick={handleSignOut}>
              Sign out
            </Link>{' '}
            and try again.
          </Text>
        </Box>
      </Stack>
    </Container>
  )
}
